// Magazyn stanu bramy POZA Reactem (USTALENIA „Technologia”): wiadomości SSE sklejane w jedno odświeżenie na
// klatkę, a każdy element ekranu subskrybuje tylko swoją część - nowy widok jednej sztuki przerysowuje jej kartę,
// nie listę. Części:
//   - skład listy (kolejność, nazwy, role) - zmienia się tylko przy wiadomości `urzadzenia`;
//   - sztuka po SN (z widokiem) - przy `widok` tej sztuki;
//   - statusy obecności (SN → status) - tylko gdy któryś status się zmienił (liczniki i filtr „Online”);
//   - stan połączenia z bramą.
import type { DeviceOut, StatusObecnosci, ViewDoc } from "../gen/kontrakt";

export type StanPolaczenia = "laczy" | "jest" | "przerwa";

type Sluchacz = () => void;

let sklad: readonly DeviceOut[] = [];
let wczytana = false;
const sztuki = new Map<string, DeviceOut>();
let statusy: Readonly<Record<string, StatusObecnosci>> = {};
let polaczenie: StanPolaczenia = "laczy";
let ostatnieDane = 0; // unix s ostatniej wiadomości z bramy - „odświeżono HH:MM” przy zerwanym połączeniu (WYGLAD.md #10)

const sluchaczeSkladu = new Set<Sluchacz>();
const sluchaczeStatusow = new Set<Sluchacz>();
const sluchaczePolaczenia = new Set<Sluchacz>();
const sluchaczeSztuk = new Map<string, Set<Sluchacz>>();

// Widoki czekające na najbliższą klatkę (nowszy widok tej samej sztuki zastępuje starszy).
const kolejka = new Map<string, ViewDoc>();
let klatka = 0;

function powiadom(zbior: Iterable<Sluchacz> | undefined): void {
  if (zbior) for (const s of zbior) s();
}

function status(widok: ViewDoc | null): StatusObecnosci {
  return widok?.presence.status ?? "unknown";
}

function przeliczStatusy(): boolean {
  const nowe: Record<string, StatusObecnosci> = {};
  let zmiana = Object.keys(statusy).length !== sztuki.size;
  for (const [sn, d] of sztuki) {
    nowe[sn] = status(d.widok);
    if (statusy[sn] !== nowe[sn]) zmiana = true;
  }
  if (zmiana) statusy = nowe;
  return zmiana;
}

function wypchnij(): void {
  klatka = 0;
  const zmienione: string[] = [];
  for (const [sn, widok] of kolejka) {
    const d = sztuki.get(sn);
    if (!d) continue;
    sztuki.set(sn, { ...d, widok });
    zmienione.push(sn);
  }
  kolejka.clear();
  for (const sn of zmienione) powiadom(sluchaczeSztuk.get(sn));
  if (przeliczStatusy()) powiadom(sluchaczeStatusow);
}

/** Pełny stan z bramy (wiadomość `urzadzenia`) albo z migawki offline (brama/migawka.ts - `czas` = kiedy zapisana,
 *  „odświeżono HH:MM” przy zerwanym połączeniu mówi, z kiedy są dane). */
export function ustawListe(lista: DeviceOut[], czas = Date.now() / 1000): void {
  kolejka.clear();
  sztuki.clear();
  for (const d of lista) sztuki.set(d.sn, d);
  sklad = lista;
  wczytana = true;
  ostatnieDane = czas;
  powiadom(sluchaczeSkladu);
  for (const zbior of sluchaczeSztuk.values()) powiadom(zbior);
  if (przeliczStatusy()) powiadom(sluchaczeStatusow);
}

/** Nowy widok jednej sztuki (wiadomość `widok`) - trafia na ekran przy najbliższej klatce. */
export function ustawWidok(sn: string, widok: ViewDoc): void {
  kolejka.set(sn, widok);
  ostatnieDane = Date.now() / 1000;
  if (!klatka) klatka = requestAnimationFrame(wypchnij);
}

export function ustawPolaczenie(stan: StanPolaczenia): void {
  if (stan === polaczenie) return;
  polaczenie = stan;
  powiadom(sluchaczePolaczenia);
}

/** Wylogowanie: magazyn pusty, następne konto zaczyna od zera. */
export function wyczysc(): void {
  if (klatka) cancelAnimationFrame(klatka);
  klatka = 0;
  kolejka.clear();
  sztuki.clear();
  sklad = [];
  wczytana = false;
  statusy = {};
  polaczenie = "laczy";
  powiadom(sluchaczeSkladu);
  powiadom(sluchaczeStatusow);
  powiadom(sluchaczePolaczenia);
  for (const zbior of sluchaczeSztuk.values()) powiadom(zbior);
}

function zapis(zbior: Set<Sluchacz>) {
  return (s: Sluchacz) => {
    zbior.add(s);
    return () => zbior.delete(s);
  };
}

export const subskrybujSklad = zapis(sluchaczeSkladu);
export const subskrybujStatusy = zapis(sluchaczeStatusow);
export const subskrybujPolaczenie = zapis(sluchaczePolaczenia);

export function subskrybujSztuke(sn: string, s: Sluchacz): () => void {
  let zbior = sluchaczeSztuk.get(sn);
  if (!zbior) sluchaczeSztuk.set(sn, (zbior = new Set()));
  zbior.add(s);
  return () => {
    zbior.delete(s);
    if (zbior.size === 0) sluchaczeSztuk.delete(sn);
  };
}

export const odczyt = {
  sklad: (): readonly DeviceOut[] => sklad,
  wczytana: (): boolean => wczytana,
  sztuka: (sn: string): DeviceOut | undefined => sztuki.get(sn),
  statusy: (): Readonly<Record<string, StatusObecnosci>> => statusy,
  polaczenie: (): StanPolaczenia => polaczenie,
  /** Unix s ostatnich danych z bramy (0 = jeszcze żadnych). */
  ostatnieDane: (): number => ostatnieDane,
};
